
import { supabase } from "@/integrations/supabase/client";
import { debugLog, DebugLevel } from "@/utils/debugUtils";

/**
 * Fetches and verifies round data from the database
 */
export const verifyRoundData = async (
  gameId: string,
  roundNumber: number,
  userWord: string,
  aiWord: string,
  aiExplanation: string | undefined
) => {
  // Verify that both words exist in the database
  // This ensures we're working with the latest data
  const { data: roundData, error: roundError } = await supabase
    .from("rounds")
    .select("user_word, ai_word, ai_explanation")
    .eq("game_id", gameId)
    .eq("round_number", roundNumber)
    .maybeSingle();
  
  if (roundError) {
    debugLog(DebugLevel.ERROR, "dataSync", "Database error when verifying words", roundError);
    // Return the original words if there's an error
    return { userWord, aiWord, aiExplanation };
  } 
  
  if (roundData) {
    // If we have data from the database, use it (it's the source of truth)
    let verifiedUserWord = userWord;
    let verifiedAiWord = aiWord;
    let verifiedAiExplanation = aiExplanation;
    
    if (roundData.user_word && roundData.user_word.trim() !== "") {
      verifiedUserWord = roundData.user_word;
      debugLog(DebugLevel.INFO, "dataSync", "Using user word from database:", verifiedUserWord);
    }
    
    if (roundData.ai_word && roundData.ai_word.trim() !== "") {
      verifiedAiWord = roundData.ai_word;
      debugLog(DebugLevel.INFO, "dataSync", "Using AI word from database:", verifiedAiWord);
      
      if (roundData.ai_explanation) {
        verifiedAiExplanation = roundData.ai_explanation;
      }
    }
    
    return { userWord: verifiedUserWord, aiWord: verifiedAiWord, aiExplanation: verifiedAiExplanation };
  }
  
  // Return the original words if no data is found
  return { userWord, aiWord, aiExplanation };
}

/**
 * Ensures a round exists in the database
 */
export const ensureRoundExists = async (
  gameId: string,
  roundNumber: number,
  userWord: string,
  aiWord: string,
  aiExplanation: string | undefined
) => {
  try {
    // If there's no data for this round, create it
    const { data: roundData, error: roundError } = await supabase
      .from("rounds")
      .select("user_word, ai_word")
      .eq("game_id", gameId)
      .eq("round_number", roundNumber)
      .maybeSingle();
    
    if (roundError) {
      throw roundError;
    }
    
    if (!roundData) {
      debugLog(DebugLevel.ERROR, "dataSync", `No round data found for round ${roundNumber}`);
      debugLog(DebugLevel.INFO, "dataSync", `Creating round ${roundNumber} with user word: ${userWord}, bot word: ${aiWord}`);
      
      // Try to create the round
      const { error: createError } = await supabase
        .from("rounds")
        .insert({ 
          game_id: gameId, 
          round_number: roundNumber,
          user_word: userWord,
          ai_word: aiWord,
          ai_explanation: aiExplanation
        });
        
      if (createError) {
        debugLog(DebugLevel.ERROR, "dataSync", `Failed to create missing round ${roundNumber}`, createError);
        return false;
      } else {
        debugLog(DebugLevel.INFO, "dataSync", `Created missing round ${roundNumber}`);
        return true;
      }
    }
    
    // If we're missing either word, update the round
    let userWordToUse = userWord.trim();
    let aiWordToUse = aiWord;
    let aiExplanationToUse = aiExplanation;
    let needsUpdate = false;
    
    // If database has user word but state doesn't, use database value
    if (!userWordToUse && roundData?.user_word) {
      userWordToUse = roundData.user_word;
      debugLog(DebugLevel.INFO, "dataSync", "Using user word from database:", userWordToUse);
    }
    
    // If state has user word but database doesn't, update database
    if (userWordToUse && !roundData?.user_word) {
      debugLog(DebugLevel.INFO, "dataSync", "User word missing from database, saving it now:", userWordToUse);
      needsUpdate = true;
    }
    
    // If database has AI word but state doesn't, use database value
    if (!aiWordToUse && roundData?.ai_word) {
      aiWordToUse = roundData.ai_word;
      debugLog(DebugLevel.INFO, "dataSync", "Using AI word from database:", aiWordToUse);
      
      // Need to retrieve AI explanation from database too
      const { data: explanationData } = await supabase
        .from("rounds")
        .select("ai_explanation")
        .eq("game_id", gameId)
        .eq("round_number", roundNumber)
        .maybeSingle();
        
      if (explanationData?.ai_explanation) {
        aiExplanationToUse = explanationData.ai_explanation;
      }
    }
    
    // If state has AI word but database doesn't, update database
    if (aiWordToUse && !roundData?.ai_word) {
      debugLog(DebugLevel.INFO, "dataSync", "Bot word missing from database, saving it now:", aiWordToUse);
      needsUpdate = true;
    }
    
    // Update the database if needed before processing
    if (needsUpdate) {
      debugLog(DebugLevel.INFO, "dataSync", "Updating database with missing word data");
      await supabase
        .from("rounds")
        .update({ 
          user_word: userWordToUse, 
          ai_word: aiWordToUse,
          ai_explanation: aiExplanationToUse
        })
        .eq("game_id", gameId)
        .eq("round_number", roundNumber);
      
      // Double-check that the update was successful
      const { data: verifyData } = await supabase
        .from("rounds")
        .select("user_word, ai_word")
        .eq("game_id", gameId)
        .eq("round_number", roundNumber)
        .maybeSingle();
      
      debugLog(DebugLevel.INFO, "dataSync", "Verification after update:", verifyData);
    }
    
    return true;
  } catch (error) {
    debugLog(DebugLevel.ERROR, "dataSync", "Error ensuring round exists:", error);
    return false;
  }
}

/**
 * Verifies that both words exist before proceeding with round processing
 */
export const verifyBothWordsExist = async (
  gameId: string, 
  roundNumber: number,
  userWord: string | undefined,
  aiWord: string | undefined
) => {
  if (!userWord || !aiWord) {
    debugLog(DebugLevel.ERROR, "dataSync", "Cannot process round: Missing user or bot word", {
      userWord,
      botWord: aiWord
    });
    
    // Try to retrieve from database again as a last resort
    const { data: lastAttemptData } = await supabase
      .from("rounds")
      .select("user_word, ai_word, ai_explanation")
      .eq("game_id", gameId)
      .eq("round_number", roundNumber)
      .maybeSingle();
    
    if (lastAttemptData?.user_word && lastAttemptData?.ai_word) {
      debugLog(DebugLevel.INFO, "dataSync", "Final database retrieval successful", {
        userWord: lastAttemptData.user_word,
        botWord: lastAttemptData.ai_word
      });
      
      return { 
        success: true, 
        userWord: lastAttemptData.user_word, 
        aiWord: lastAttemptData.ai_word, 
        aiExplanation: lastAttemptData.ai_explanation 
      };
    } else {
      debugLog(DebugLevel.ERROR, "dataSync", "Final database retrieval failed. Cannot continue.");
      return { success: false, userWord: null, aiWord: null, aiExplanation: null };
    }
  }
  
  return { success: true, userWord, aiWord, aiExplanation: undefined };
}
